/**
 * PlumeVoxelCanvasLayer - MODFLOW-style voxel grid renderer for Leaflet
 *
 * Renders plume concentrations as a uniform grid with IDW interpolation.
 * Matches the 3D voxel grid visualization for consistency.
 *
 * Key features:
 * - IDW (Inverse Distance Weighting) interpolation from well data
 * - Edge feathering for smooth, organic boundaries
 * - Log-scale concentration mapping
 * - Same color/opacity formulas as 3D visualization
 */

import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import { useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';

// ============================================================================
// TYPES
// ============================================================================

export interface VoxelWell {
  lat: number;
  lon: number;
  concentration: number;
}

export interface VoxelCleanWell {
  lat: number;
  lon: number;
}

export interface VoxelPlumeData {
  /** Unique identifier for this plume */
  id: string;
  /** Polygon boundary as [lat, lon] pairs */
  boundary: [number, number][];
  /** Wells with concentration data */
  wells: VoxelWell[];
  /** Clean wells (exclusion zones) */
  cleanWells?: VoxelCleanWell[];
  /** Maximum concentration for normalization */
  maxConcentration: number;
  /** Base color for the plume (hex) */
  baseColor: string;
  /** Compound name (for debugging) */
  compound?: string;
}

export interface PlumeVoxelCanvasLayerProps {
  /** Array of plume data to render */
  plumes: VoxelPlumeData[];
  /** Grid resolution (default 160 = 160x160 grid per plume) */
  gridResolution?: number;
  /** Edge feathering distance in feet (default 15) */
  edgeFeatherFt?: number;
  /** Base opacity multiplier (default 1) */
  baseOpacity?: number;
  /** Whether the layer is visible */
  visible?: boolean;
  /** Z-index for layer ordering */
  zIndex?: number;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const FT_PER_DEG_LAT = 364000;
const CLEAN_WELL_EXCLUSION_FT = 75;

// ============================================================================
// BOUNDARY SMOOTHING (Catmull-Rom Spline)
// ============================================================================

/**
 * Smooth boundary points using Chaikin's corner-cutting algorithm.
 * Unlike Catmull-Rom (which passes through original vertices and preserves sharp corners),
 * Chaikin's algorithm actually rounds off corners by replacing each edge with two new points
 * at the 25% and 75% positions. This converges to a quadratic B-spline curve.
 *
 * @param points - Array of [lat, lon] boundary points (closed polygon)
 * @param iterations - Number of subdivision iterations (3-4 recommended for convex hulls)
 * @returns Smoothed array of [lat, lon] points with rounded corners
 */
function smoothBoundaryPoints(
  points: [number, number][],
  iterations: number = 4,
  _tension?: number  // kept for API compat, unused by Chaikin
): [number, number][] {
  if (points.length < 3) return points;

  let smoothed = [...points];

  for (let iter = 0; iter < iterations; iter++) {
    const newPoints: [number, number][] = [];
    const n = smoothed.length;

    for (let i = 0; i < n; i++) {
      const p1 = smoothed[i];
      const p2 = smoothed[(i + 1) % n];

      // Chaikin: cut corners at 25% and 75% of each edge
      newPoints.push([
        0.75 * p1[0] + 0.25 * p2[0],
        0.75 * p1[1] + 0.25 * p2[1],
      ]);
      newPoints.push([
        0.25 * p1[0] + 0.75 * p2[0],
        0.25 * p1[1] + 0.75 * p2[1],
      ]);
    }

    smoothed = newPoints;
  }

  return smoothed;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function ftPerDegLon(lat: number): number {
  return FT_PER_DEG_LAT * Math.cos(lat * Math.PI / 180);
}

/**
 * Check if a point is inside a polygon using ray casting
 */
function isInsidePolygon(lat: number, lon: number, boundary: [number, number][]): boolean {
  let inside = false;
  for (let i = 0, j = boundary.length - 1; i < boundary.length; j = i++) {
    const [yi, xi] = boundary[i];
    const [yj, xj] = boundary[j];
    if (((yi > lat) !== (yj > lat)) && (lon < (xj - xi) * (lat - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  return inside;
}

/**
 * Calculate distance from a point to the polygon boundary (in feet)
 */
function distanceToPolygonEdge(
  lat: number,
  lon: number,
  boundary: [number, number][],
  centerLat: number
): number {
  const lonFactor = ftPerDegLon(centerLat);
  let minDist = Infinity;

  for (let i = 0, j = boundary.length - 1; i < boundary.length; j = i++) {
    const [y1, x1] = boundary[i];
    const [y2, x2] = boundary[j];

    // Convert to feet
    const px = lon * lonFactor;
    const py = lat * FT_PER_DEG_LAT;
    const ax = x1 * lonFactor;
    const ay = y1 * FT_PER_DEG_LAT;
    const bx = x2 * lonFactor;
    const by = y2 * FT_PER_DEG_LAT;

    // Vector from a to b
    const abx = bx - ax;
    const aby = by - ay;
    const abLen2 = abx * abx + aby * aby;

    if (abLen2 < 0.001) {
      const dx = px - ax;
      const dy = py - ay;
      minDist = Math.min(minDist, Math.sqrt(dx * dx + dy * dy));
      continue;
    }

    // Project point onto line segment
    const t = Math.max(0, Math.min(1, ((px - ax) * abx + (py - ay) * aby) / abLen2));
    const closestX = ax + t * abx;
    const closestY = ay + t * aby;

    const dx = px - closestX;
    const dy = py - closestY;
    minDist = Math.min(minDist, Math.sqrt(dx * dx + dy * dy));
  }

  return minDist;
}

/**
 * IDW interpolation of concentration from well data
 */
function interpolateConcentration(
  lat: number,
  lon: number,
  wells: VoxelWell[],
  centerLat: number,
  maxConcentration: number
): number {
  if (wells.length === 0) return maxConcentration * 0.5;

  const lonFactor = ftPerDegLon(centerLat);
  let sumWeights = 0;
  let sumWeightedConc = 0;
  const power = 2;

  for (const well of wells) {
    const dLat = (lat - well.lat) * FT_PER_DEG_LAT;
    const dLon = (lon - well.lon) * lonFactor;
    const dist = Math.sqrt(dLat * dLat + dLon * dLon);
    if (dist < 1) return well.concentration;
    const weight = 1 / Math.pow(dist, power);
    sumWeights += weight;
    sumWeightedConc += weight * well.concentration;
  }

  return sumWeights > 0 ? sumWeightedConc / sumWeights : 0;
}

/**
 * Check if point is near a clean well (exclusion zone)
 */
function isNearCleanWell(
  lat: number,
  lon: number,
  cleanWells: VoxelCleanWell[],
  centerLat: number
): boolean {
  if (cleanWells.length === 0) return false;
  const lonFactor = ftPerDegLon(centerLat);

  for (const well of cleanWells) {
    const dLat = (lat - well.lat) * FT_PER_DEG_LAT;
    const dLon = (lon - well.lon) * lonFactor;
    const distFt = Math.sqrt(dLat * dLat + dLon * dLon);
    if (distFt < CLEAN_WELL_EXCLUSION_FT) return true;
  }
  return false;
}

/**
 * Log-scale concentration ratio (matches 3D)
 */
function getConcentrationRatio(concentration: number, maxConcentration: number): number {
  if (maxConcentration <= 0) return 0;
  const logMax = Math.log10(maxConcentration + 1);
  const logConc = Math.log10(concentration + 1);
  return Math.min(1, Math.max(0, logConc / logMax));
}

/**
 * Get color based on concentration (matches 3D formula)
 */
function getConcentrationColor(concentration: number, maxConcentration: number, baseColor: string): string {
  const ratio = getConcentrationRatio(concentration, maxConcentration);
  const hex = baseColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // EXACT formula from 3D: darken at low concentration, blend with white
  const darkFactor = 0.15 + (1 - ratio) * 0.75;
  const lightBlend = (1 - ratio) * 0.7;

  const finalR = Math.min(255, Math.round(r * darkFactor + lightBlend * 255));
  const finalG = Math.min(255, Math.round(g * darkFactor + lightBlend * 255));
  const finalB = Math.min(255, Math.round(b * darkFactor + lightBlend * 255));

  return `rgb(${finalR}, ${finalG}, ${finalB})`;
}

/**
 * Get opacity based on concentration — solid fill with subtle gradient
 */
function getConcentrationOpacity(concentration: number, maxConcentration: number): number {
  const ratio = getConcentrationRatio(concentration, maxConcentration);
  return 0.7 + ratio * 0.25;
}

// ============================================================================
// VOXEL GRID GENERATION
// ============================================================================

interface Voxel2D {
  lat: number;
  lon: number;
  color: string;
  opacity: number;
}

function generateVoxelGrid(
  plume: VoxelPlumeData,
  gridResolution: number,
  edgeFeatherFt: number
): Voxel2D[] {
  const { boundary: rawBoundary, wells, cleanWells = [], maxConcentration, baseColor } = plume;

  if (rawBoundary.length < 3 || wells.length === 0) return [];

  // If boundary has many points (>10), it was already smoothed by the backend — use as-is.
  // Only apply Chaikin smoothing to raw convex hulls (few vertices) from client-side generation.
  const boundary = rawBoundary.length <= 10
    ? smoothBoundaryPoints(rawBoundary, 4)
    : rawBoundary;

  const voxels: Voxel2D[] = [];

  // Calculate bounds from smoothed boundary
  const lats = boundary.map(p => p[0]);
  const lons = boundary.map(p => p[1]);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLon = Math.min(...lons);
  const maxLon = Math.max(...lons);

  const centerLat = (minLat + maxLat) / 2;
  const latStep = (maxLat - minLat) / gridResolution;
  const lonStep = (maxLon - minLon) / gridResolution;

  for (let i = 0; i <= gridResolution; i++) {
    for (let j = 0; j <= gridResolution; j++) {
      const lat = minLat + i * latStep;
      const lon = minLon + j * lonStep;

      if (isInsidePolygon(lat, lon, boundary)) {
        const conc = interpolateConcentration(lat, lon, wells, centerLat, maxConcentration);
        const ratio = getConcentrationRatio(conc, maxConcentration);

        // Skip very low concentration cells
        if (ratio < 0.02) continue;

        // Edge feathering - fade out near polygon boundary
        const edgeDist = distanceToPolygonEdge(lat, lon, boundary, centerLat);
        const edgeFactor = Math.min(1, edgeDist / edgeFeatherFt);
        // Smoothstep for natural falloff
        const smoothEdgeFactor = edgeFactor * edgeFactor * (3 - 2 * edgeFactor);

        const baseOpacity = getConcentrationOpacity(conc, maxConcentration);

        voxels.push({
          lat,
          lon,
          color: getConcentrationColor(conc, maxConcentration, baseColor),
          opacity: baseOpacity * smoothEdgeFactor,
        });
      }
    }
  }

  return voxels;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function PlumeVoxelCanvasLayer({
  plumes,
  gridResolution = 160,
  edgeFeatherFt = 15,
  baseOpacity = 1,
  visible = true,
  zIndex = 400,
}: PlumeVoxelCanvasLayerProps) {
  const map = useMap();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Pre-generate all voxels for all plumes
  const allVoxels = useMemo(() => {
    if (!visible || plumes.length === 0) return [];

    const voxels: Voxel2D[] = [];
    for (const plume of plumes) {
      const plumeVoxels = generateVoxelGrid(plume, gridResolution, edgeFeatherFt);
      voxels.push(...plumeVoxels);
    }

    return voxels;
  }, [plumes, gridResolution, edgeFeatherFt, visible]);

  // Create canvas once inside a custom Leaflet pane (below markerPane z:600, above overlayPane z:400)
  // This ensures wells and well logs render IN FRONT of plume layers.
  // Canvas persists across visibility toggles — we toggle display instead of destroying DOM.
  useEffect(() => {
    if (!map) return;

    // Create custom pane if it doesn't exist
    const paneName = 'plumeCanvasPane';
    if (!map.getPane(paneName)) {
      const pane = map.createPane(paneName);
      pane.style.zIndex = '450';
      pane.style.pointerEvents = 'none';
    }
    const pane = map.getPane(paneName)!;

    const container = L.DomUtil.create('div', 'plume-voxel-canvas-container');
    container.style.position = 'absolute';
    container.style.top = '0';
    container.style.left = '0';
    container.style.pointerEvents = 'none';
    container.style.overflow = 'hidden';

    const canvas = document.createElement('canvas');
    canvas.style.position = 'absolute';
    canvas.style.top = '0';
    canvas.style.left = '0';
    container.appendChild(canvas);

    pane.appendChild(container);

    canvasRef.current = canvas;
    containerRef.current = container;

    return () => {
      if (container.parentNode) {
        container.parentNode.removeChild(container);
      }
      canvasRef.current = null;
      containerRef.current = null;
    };
  }, [map]);

  // Toggle visibility via CSS display instead of DOM destroy/recreate
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.style.display = visible ? '' : 'none';
    }
  }, [visible]);

  // Draw voxels to canvas
  const drawVoxels = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container || !map || !visible || allVoxels.length === 0) {
      return;
    }

    const size = map.getSize();
    const bounds = map.getBounds();

    // Offset container within the pane to align with the viewport
    // containerPointToLayerPoint maps container (0,0) to layer-space coords,
    // counteracting the map pane's transform so containerPoint drawing works
    const layerOffset = map.containerPointToLayerPoint(L.point(0, 0));
    container.style.transform = `translate(${layerOffset.x}px, ${layerOffset.y}px)`;
    container.style.width = `${size.x}px`;
    container.style.height = `${size.y}px`;

    canvas.width = size.x;
    canvas.height = size.y;
    canvas.style.width = `${size.x}px`;
    canvas.style.height = `${size.y}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const zoom = map.getZoom();
    const metersPerPixel = 156543.03 * Math.cos(bounds.getCenter().lat * Math.PI / 180) / Math.pow(2, zoom);

    // Calculate typical cell size from first plume
    let cellSizePixels = 4;
    if (plumes.length > 0) {
      const plume = plumes[0];
      const lats = plume.boundary.map(p => p[0]);
      const lons = plume.boundary.map(p => p[1]);
      const latRange = Math.max(...lats) - Math.min(...lats);
      const lonRange = Math.max(...lons) - Math.min(...lons);
      const avgRangeDeg = (latRange + lonRange) / 2;
      const cellSizeDeg = avgRangeDeg / gridResolution;
      const cellSizeMeters = cellSizeDeg * 111320;
      cellSizePixels = Math.max(2, Math.min(20, cellSizeMeters / metersPerPixel * 1.2));
    }

    // Visible bounds with buffer
    const latBuffer = (bounds.getNorth() - bounds.getSouth()) * 0.1;
    const lonBuffer = (bounds.getEast() - bounds.getWest()) * 0.1;
    const visibleBounds = {
      north: bounds.getNorth() + latBuffer,
      south: bounds.getSouth() - latBuffer,
      east: bounds.getEast() + lonBuffer,
      west: bounds.getWest() - lonBuffer,
    };

    // Pre-compute affine transform: lat/lon → pixel using two reference points
    // This avoids calling map.latLngToContainerPoint() per voxel (128K+ calls)
    const refSW = map.latLngToContainerPoint([visibleBounds.south, visibleBounds.west]);
    const refNE = map.latLngToContainerPoint([visibleBounds.north, visibleBounds.east]);
    const latRange = visibleBounds.north - visibleBounds.south;
    const lonRange = visibleBounds.east - visibleBounds.west;
    const pxPerLat = (refNE.y - refSW.y) / latRange;  // negative (north is up)
    const pxPerLon = (refNE.x - refSW.x) / lonRange;
    const originX = refSW.x;
    const originY = refSW.y;
    const originLat = visibleBounds.south;
    const originLon = visibleBounds.west;

    // Draw voxels using affine transform
    let drawnCount = 0;
    for (const voxel of allVoxels) {
      if (
        voxel.lat < visibleBounds.south ||
        voxel.lat > visibleBounds.north ||
        voxel.lon < visibleBounds.west ||
        voxel.lon > visibleBounds.east
      ) {
        continue;
      }

      const px = originX + (voxel.lon - originLon) * pxPerLon;
      const py = originY + (voxel.lat - originLat) * pxPerLat;

      ctx.fillStyle = voxel.color;
      ctx.globalAlpha = voxel.opacity * baseOpacity;

      const halfSize = cellSizePixels / 2;
      ctx.fillRect(px - halfSize, py - halfSize, cellSizePixels, cellSizePixels);
      drawnCount++;
    }

    ctx.globalAlpha = 1;
  }, [map, allVoxels, plumes, gridResolution, baseOpacity, visible]);

  // Redraw on map events — only on moveend to avoid per-pixel redraws
  useMapEvents({
    moveend: drawVoxels,
    zoomend: drawVoxels,
    resize: drawVoxels,
  });

  // Initial draw, redraw on voxels change, and handle visibility toggle.
  // Single effect to avoid double drawVoxels when visibility changes.
  useEffect(() => {
    if (visible) {
      const timer = setTimeout(drawVoxels, 50);
      return () => clearTimeout(timer);
    } else if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }
    }
  }, [drawVoxels, allVoxels, visible]);

  return null;
}
